import React, { useMemo } from 'react';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';

import { Checkbox, Table } from '@savgroup-front-common/core';
import { Link } from '@savgroup-front-common/core/src/atoms/link';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';
import { UI_SIZE } from '@savgroup-front-common/constants/src/shared/size';

import type { DossierRow } from '../../types/dossier.types';
import { formatDossierDate } from '../../utils/dossierFormat';
import { getDossierStatutTagVariant } from '../../utils/dossierStatut';
import { buildDossierSummaryPath } from '../../utils/dossierRoutes';
import { useDossierSelection } from '../../pages/DossierPage.context';
import { $DossierTableScrollArea } from '../../pages/DossierPage.styles';

const checkboxCellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  width: '100%',
};

const SelectionHeaderCheckbox = () => {
  const { allSelected, someSelected, toggleSelectAll } = useDossierSelection();

  return (
    <div style={checkboxCellStyle}>
      <Checkbox
        name="select-all-dossiers"
        checked={allSelected}
        isIndeterminate={someSelected}
        onChange={() => toggleSelectAll()}
        ariaLabel="Tout sélectionner"
        dataTestId="dossiers-select-all"
        withoutMargin
      />
    </div>
  );
};

const SelectionRowCheckbox = ({ numeroDossier }: { numeroDossier: string }) => {
  const { isSelected, toggleSelection } = useDossierSelection();
  const checked = isSelected(numeroDossier);

  return (
    <div style={checkboxCellStyle}>
      <Checkbox
        name={`select-dossier-${numeroDossier}`}
        checked={checked}
        onChange={() => toggleSelection(numeroDossier)}
        ariaLabel={`Sélectionner le dossier ${numeroDossier}`}
        dataTestId={`dossiers-select-${numeroDossier}`}
        withoutMargin
      />
    </div>
  );
};

export interface DossierTableViewProps {
  dossiers: DossierRow[];
  dataTestId?: string;
}

const CENTERED_CELL_PROPS = {
  style: { verticalAlign: 'middle' as const },
};

const SELECTION_CELL_PROPS = {
  style: { textAlign: 'center' as const, verticalAlign: 'middle' as const },
};

const DossierTableView = ({ dossiers, dataTestId = 'dossiers-table' }: DossierTableViewProps) => {
  const navigate = useNavigate();

  const columns = useMemo<Column<DossierRow>[]>(
    () => [
      {
        id: 'selection',
        width: 48,
        disableSortBy: true,
        getProps: () => SELECTION_CELL_PROPS,
        Header: () => <SelectionHeaderCheckbox />,
        Cell: ({ row }: { row: { original: DossierRow } }) => (
          <SelectionRowCheckbox numeroDossier={row.original.numeroDossier} />
        ),
      },
      {
        Header: 'N° dossier',
        accessor: 'numeroDossier',
        Cell: ({ row }: { row: { original: DossierRow } }) => (
          <Link
            href={buildDossierSummaryPath(row.original.fileId)}
            onClick={(event) => {
              event.preventDefault();
              navigate(buildDossierSummaryPath(row.original.fileId));
            }}
          >
            {row.original.numeroDossier}
          </Link>
        ),
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Date de création',
        accessor: 'dateCreation',
        Cell: ({ value }: { value: string }) => formatDossierDate(value),
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Date dernière modif.',
        accessor: 'dateDerniereModif',
        Cell: ({ value }: { value: string }) => formatDossierDate(value),
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Statut',
        accessor: 'statut',
        Cell: ({ value }: { value: string }) => (
          <Tag variant={getDossierStatutTagVariant(value)} size={UI_SIZE.SM} withoutMargin>
            {value}
          </Tag>
        ),
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Motif',
        accessor: 'motif',
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Garantie',
        accessor: 'garantie',
        getProps: () => CENTERED_CELL_PROPS,
      },
      {
        Header: 'Intervenant',
        accessor: 'intervenant',
        getProps: () => CENTERED_CELL_PROPS,
      },
    ],
    [navigate],
  );

  return (
    <$DossierTableScrollArea>
      <Table<DossierRow> columns={columns} data={dossiers} dataTestId={dataTestId} />
    </$DossierTableScrollArea>
  );
};

export default DossierTableView;
